import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle_filled.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle_filled.svg";
import LiquidonateLogo from "@redotech/redo-icons/liquidonate.svg";
import { RedoIntegrationsRpcClientContext } from "@redotech/redo-merchant-app-common/integrations-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { DonateState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { useQuery } from "@tanstack/react-query";
import { memo, useContext, useState } from "react";
import { LocationOptionsContext } from "../flows/location-options-context";
import {
  DonationProviderSetup,
  DonationProviderSetupModal,
} from "./donation-provider-setup-modal";
import { LocationCard } from "./location-card";
import { useLocationSelectionHandler } from "./use-location-selection-handler";

export const DonateDetails = memo(function DonateDetails({
  state,
  setState,
}: StepTypeDetailsProps<DonateState>) {
  const { t } = useTranslation();
  const locationOptions = useContext(LocationOptionsContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const integrationsRpcClient = useRequiredContext(
    RedoIntegrationsRpcClientContext,
  );

  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);

  const providerQuery = useQuery({
    queryKey: ["donationProvider"],
    queryFn: ({ signal }) =>
      integrationsRpcClient.getDonationProvider({}, { signal }),
  });

  const resolvedLocations = useLoad(
    async (signal) => {
      const addressIds = state.locations.filter((id) => id !== "");

      if (addressIds.length === 0) {
        return { addresses: [], externalMap: new Map<string, string>() };
      }

      const result = await rpcClient.getAddressesByIds(
        { ids: addressIds },
        { signal },
      );

      const externalMap = new Map<string, string>();
      for (const addr of result.addresses) {
        if (addr.type !== "custom" && addr.externalId) {
          externalMap.set(addr._id.toString(), addr.externalId);
        }
      }

      return { addresses: result.addresses, externalMap };
    },
    [state.locations, rpcClient],
  );

  const handleAddLocation = () => {
    setState((prev) => ({ ...prev, locations: [...prev.locations, ""] }));
  };

  const handleDeleteLocation = (addressId: string) => {
    setState((prev) => ({
      ...prev,
      locations: prev.locations.filter((id) => id !== addressId),
    }));
  };

  const handleLocationSelect = useLocationSelectionHandler(setState);

  const handleRestockLocationSelect = (
    addressId: string,
    restockLocationId: string | null,
  ) => {
    setState((prev) => ({
      ...prev,
      restockExternalLocationIds: {
        ...prev.restockExternalLocationIds,
        [addressId]: restockLocationId,
      },
    }));
  };

  const handleSetupProvider = async (setup: DonationProviderSetup) => {
    const result = await integrationsRpcClient.setupDonationProvider(setup);
    setSetupComplete(result.enabled);
  };

  if (!locationOptions) {
    return null;
  }

  if (providerQuery.isError) {
    return (
      <Flex align="center" dir="column" gap="lg" py="2xl">
        <Flex align="center" gap="sm">
          <Icon
            arbiterIconSvg={AlertTriangleIcon}
            color="error"
            size="compact"
          />
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Couldn't load donation provider settings")}
          </Text>
        </Flex>
        <Text fontSize="xs" textColor="secondary">
          {t("Something went wrong loading this section. Please try again.")}
        </Text>
        <RedoButton
          hierarchy="secondary"
          onClick={() => providerQuery.refetch()}
          pending={providerQuery.isFetching}
          size="xs"
          text={t("Retry")}
        />
      </Flex>
    );
  }

  if (providerQuery.isPending) {
    return null;
  }

  const enabled = setupComplete || providerQuery.data.enabled;

  if (!enabled) {
    return (
      <Flex dir="column" gap="lg">
        <Text fontSize="xs" textColor="secondary">
          {t(
            "Set up a donation provider to route eligible returns to a nearby nonprofit.",
          )}
        </Text>
        <Flex justify="flex-start">
          <RedoButton
            hierarchy="primary"
            onClick={() => setSetupModalOpen(true)}
            size="xs"
            text={t("Set up a donation provider")}
          />
        </Flex>
        <DonationProviderSetupModal
          defaults={providerQuery.data.defaults}
          onConfirm={handleSetupProvider}
          open={setupModalOpen}
          setOpen={setSetupModalOpen}
        />
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="md">
      <LiquidonateLogo />
      <Flex align="center" gap="sm">
        <Icon arbiterIconSvg={CheckCircleIcon} color="success" size="compact" />
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("Donation provider enabled")}
        </Text>
      </Flex>
      <Text fontSize="xs" textColor="secondary">
        {t(
          "Items will be routed to a nearby nonprofit via donation matching. If matching fails, items will ship to the fallback location.",
        )}
      </Text>
      <Flex dir="column" gap="3xl">
        {state.locations.map((locationId, index) => (
          <LocationCard
            addressId={locationId}
            cardTitle={t("Fallback location {{number}}", { number: index + 1 })}
            externalLocationId={resolvedLocations.value?.externalMap.get(
              locationId,
            )}
            index={index}
            key={`${locationId}-${index}`}
            locationOptions={locationOptions}
            onDelete={handleDeleteLocation}
            onLocationSelect={(newAddressId, newExternalLocationId) =>
              handleLocationSelect(
                locationId,
                newAddressId,
                newExternalLocationId,
                index,
              )
            }
            onRestockLocationSelect={handleRestockLocationSelect}
            resolvedAddresses={resolvedLocations.value?.addresses ?? []}
            restockExternalLocationId={
              state.restockExternalLocationIds?.[locationId]
            }
            selectedLocations={state.locations}
          />
        ))}
      </Flex>
      <Flex justify="flex-start" mt="2xl">
        <RedoButton
          hierarchy="secondary"
          IconLeading={() => <>+</>}
          onClick={handleAddLocation}
          size="xs"
          text={t("Add fallback location")}
        />
      </Flex>
    </Flex>
  );
});
